import { clearAuthSession, getAccessToken, getTokenType } from "@/utils/auth";

export interface ApiResult<T> {
  code: string;
  data: T;
  msg?: string;
}

export interface PageData<T> {
  list: T[];
  total: number;
}

export interface PageResult<T> {
  code: string;
  data: PageData<T>;
  msg?: string;
}

export interface MenuRecord {
  id: number;
  parentId?: number;
  name: string;
  type: number;
  routeName?: string;
  routePath?: string;
  component?: string;
  sort?: number;
  visible?: number;
  icon?: string;
  redirect?: string;
  perm?: string;
  children?: MenuRecord[];
}

export interface MenuFormData {
  id?: number;
  parentId?: number;
  name: string;
  type: number;
  routeName?: string;
  routePath?: string;
  component?: string;
  perm?: string;
  visible: number;
  sort?: number;
  icon?: string;
  redirect?: string;
  keepAlive?: number;
  alwaysShow?: number;
  params?: { key: string; value: string }[];
}

export interface Option<T = string | number> {
  label: string;
  value: T;
  children?: Option<T>[];
}

export interface DictRecord {
  id: number;
  name: string;
  dictCode: string;
  status: number;
  remark?: string;
}

export interface DictFormData {
  id?: number;
  name: string;
  dictCode: string;
  status: number;
  remark?: string;
}

export interface DictItemRecord {
  id: number;
  dictCode: string;
  label: string;
  value: string;
  sort?: number;
  status: number;
  tagType?: string;
}

export interface DictItemFormData {
  id?: number;
  dictCode?: string;
  label: string;
  value: string;
  sort?: number;
  status: number;
  tagType?: string;
}

export interface AuthenticationToken {
  tokenType: string;
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

export interface CaptchaInfo {
  captchaKey: string;
  captchaBase64: string;
}

export interface UserRecord {
  id: number;
  username: string;
  nickname: string;
  mobile?: string;
  gender?: number;
  avatar?: string;
  email?: string;
  status: number;
  deptName?: string;
  roleNames?: string;
  createTime?: string;
}

export interface UserProfile {
  id: number;
  username: string;
  nickname?: string;
  avatar?: string;
  mobile?: string;
  email?: string;
  deptName?: string;
  roleNames?: string;
  status?: number;
  createTime?: string;
}

export interface MenuRoute {
  path?: string;
  component?: string;
  redirect?: string;
  name?: string;
  meta?: {
    title?: string;
    icon?: string;
    hidden?: boolean;
    alwaysShow?: boolean;
    params?: Record<string, string>;
  };
  children?: MenuRoute[];
}

export interface PasswordUpdateData {
  oldPassword: string;
  newPassword: string;
}

export interface UserFormData {
  id?: number;
  username: string;
  nickname: string;
  mobile?: string;
  gender?: number;
  avatar?: string;
  email?: string;
  status: number;
  deptId?: number;
  roleIds: number[];
  openId?: string;
}

export interface RoleRecord {
  id: number;
  name: string;
  code: string;
  status: number;
  sort?: number;
  createTime?: string;
  updateTime?: string;
}

export interface RoleFormData {
  id?: number;
  name: string;
  code: string;
  sort?: number;
  status: number;
  dataScope?: number;
}

export interface LogRecord {
  id: number;
  module?: string;
  content?: string;
  requestUri?: string;
  method?: string;
  ip?: string;
  region?: string;
  browser?: string;
  os?: string;
  executionTime?: number;
  createBy?: number;
  createTime?: string;
  operator?: string;
}

export class ApiError extends Error {
  code?: string;
  status?: number;
  data?: unknown;

  constructor(message: string, options: { code?: string; status?: number; data?: unknown } = {}) {
    super(message);
    this.name = "ApiError";
    this.code = options.code;
    this.status = options.status;
    this.data = options.data;
  }
}

const API_BASE = import.meta.env.VITE_API_BASE_URL || "";
type FetchOptions = Parameters<typeof fetch>[1];

export function toQuery(params?: Record<string, unknown>) {
  const searchParams = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      searchParams.append(key, String(value));
    }
  });
  const query = searchParams.toString();
  return query ? `?${query}` : "";
}

function getPayloadMessage(payload: any, fallback: string) {
  if (!payload) return fallback;
  if (typeof payload === "string") return payload;
  return payload.msg || payload.message || payload.error || fallback;
}

export async function request<T>(url: string, options: FetchOptions = {}): Promise<T> {
  const token = getAccessToken();
  const headers = new Headers(options.headers);

  if (!headers.has("Content-Type") && options.body && !(options.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }
  if (token && !headers.has("Authorization")) {
    headers.set("Authorization", `${getTokenType()} ${token}`);
  }

  const response = await fetch(`${API_BASE}${url}`, {
    ...options,
    headers,
  });

  const payload = await response.json().catch(() => null);

  if (!response.ok) {
    const isLoginRequest = url.startsWith("/api/v1/auth/login");
    const errorMessage = response.status === 413
      ? "上传文件过大，单个文件不能超过20MB，请压缩后重新上传"
      : getPayloadMessage(payload, `请求失败 (${response.status})`);

    if (response.status === 401 && !isLoginRequest) {
      clearAuthSession();
      if (window.REACT_APP_NAVIGATE) {
        window.REACT_APP_NAVIGATE("/login");
      }
    }

    throw new ApiError(errorMessage, {
      code: payload?.code,
      status: response.status,
      data: payload?.data,
    });
  }

  if (payload?.code && payload.code !== "00000") {
    throw new ApiError(getPayloadMessage(payload, "请求失败"), {
      code: payload.code,
      status: response.status,
      data: payload.data,
    });
  }

  return payload;
}

export async function downloadFile(
  url: string,
  fallbackFileName: string,
  options: FetchOptions = {},
) {
  const token = getAccessToken();
  const headers = new Headers(options.headers);
  if (token && !headers.has("Authorization")) {
    headers.set("Authorization", `${getTokenType()} ${token}`);
  }

  const response = await fetch(`${API_BASE}${url}`, { ...options, headers });
  if (!response.ok) {
    const payload = await response.json().catch(() => null);
    throw new ApiError(getPayloadMessage(payload, `下载失败 (${response.status})`), {
      code: payload?.code,
      status: response.status,
      data: payload?.data,
    });
  }

  const disposition = response.headers.get("Content-Disposition") || "";
  const encodedName = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  const plainName = disposition.match(/filename="?([^";]+)"?/i)?.[1];
  let fileName = fallbackFileName;
  try {
    fileName = decodeURIComponent(encodedName || plainName || fallbackFileName);
  } catch {
    fileName = fallbackFileName;
  }

  const blobUrl = URL.createObjectURL(await response.blob());
  const link = document.createElement("a");
  link.href = blobUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(blobUrl);
}

export async function listMenus(params?: { keywords?: string; status?: number }) {
  const res = await request<ApiResult<MenuRecord[]>>(`/api/v1/menus${toQuery(params)}`);
  return res.data || [];
}

export async function pageUsers(params?: {
  pageNum?: number;
  pageSize?: number;
  keywords?: string;
  status?: number;
  roleIds?: number[];
}) {
  const res = await request<PageResult<UserRecord>>(`/api/v1/users/page${toQuery(params)}`);
  return res.data || { list: [], total: 0 };
}

let cachedMenuRoutes: MenuRoute[] | undefined;
let cachedMenuToken = "";
let menuRoutesRequest: Promise<MenuRoute[]> | undefined;

export function getCachedCurrentUserMenuRoutes() {
  const token = getAccessToken();
  return token && token === cachedMenuToken ? cachedMenuRoutes : undefined;
}

export function invalidateCurrentUserMenuRoutes() {
  cachedMenuRoutes = undefined;
  cachedMenuToken = "";
  menuRoutesRequest = undefined;
  window.dispatchEvent(new Event("menu-routes-changed"));
}

export function getCurrentUserMenuRoutes() {
  const token = getAccessToken();
  if (token === cachedMenuToken && cachedMenuRoutes) {
    return Promise.resolve(cachedMenuRoutes);
  }
  if (token === cachedMenuToken && menuRoutesRequest) {
    return menuRoutesRequest;
  }

  cachedMenuToken = token;
  cachedMenuRoutes = undefined;
  menuRoutesRequest = request<ApiResult<MenuRoute[]>>("/api/v1/menus/routes")
    .then((res) => {
      cachedMenuRoutes = res.data || [];
      return cachedMenuRoutes;
    })
    .finally(() => {
      menuRoutesRequest = undefined;
    });
  return menuRoutesRequest;
}

export function exportUsers(params?: {
  keywords?: string;
  status?: number;
  roleIds?: number[];
}) {
  return downloadFile(`/api/v1/users/export${toQuery(params)}`, "用户列表.xlsx");
}

export function exportAdminData(
  type: string,
  params?: Record<string, unknown>,
  fallbackFileName = "导出数据.xlsx",
) {
  return downloadFile(`/api/admin/exports/${type}${toQuery(params)}`, fallbackFileName);
}

export async function getUserForm(id: number) {
  const res = await request<ApiResult<UserFormData>>(`/api/v1/users/${id}/form`);
  return res.data;
}

export async function getUserProfile() {
  const res = await request<ApiResult<UserProfile>>("/api/v1/users/profile");
  return res.data;
}

export function changePassword(data: PasswordUpdateData) {
  return request<ApiResult<null>>("/api/v1/users/password", {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export function createUser(data: UserFormData) {
  return request<ApiResult<null>>("/api/v1/users", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export function updateUser(id: number, data: UserFormData) {
  return request<ApiResult<null>>(`/api/v1/users/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export function deleteUsers(ids: number[]) {
  return request<ApiResult<null>>(`/api/v1/users/${ids.join(",")}`, {
    method: "DELETE",
  });
}

export function updateUserStatus(id: number, status: number) {
  return request<ApiResult<null>>(`/api/v1/users/${id}/status${toQuery({ status })}`, {
    method: "PATCH",
  });
}

export function resetUserPassword(id: number, password: string) {
  return request<ApiResult<null>>(`/api/v1/users/${id}/password/reset${toQuery({ password })}`, {
    method: "PUT",
  });
}

export async function pageRoles(params?: {
  pageNum?: number;
  pageSize?: number;
  keywords?: string;
}) {
  const res = await request<PageResult<RoleRecord>>(`/api/v1/roles/page${toQuery(params)}`);
  return res.data || { list: [], total: 0 };
}

export async function listRoleOptions() {
  const res = await request<ApiResult<Option<number>[]>>("/api/v1/roles/options");
  return res.data || [];
}

export async function getRoleForm(id: number) {
  const res = await request<ApiResult<RoleFormData>>(`/api/v1/roles/${id}/form`);
  return res.data;
}

export function createRole(data: RoleFormData) {
  return request<ApiResult<null>>("/api/v1/roles", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export function updateRole(id: number, data: RoleFormData) {
  return request<ApiResult<null>>(`/api/v1/roles/${id}`, {
    method: "PUT",
    body: JSON.stringify({ ...data, id }),
  });
}

export function deleteRoles(ids: number[]) {
  return request<ApiResult<null>>(`/api/v1/roles/${ids.join(",")}`, {
    method: "DELETE",
  });
}

export function updateRoleStatus(id: number, status: number) {
  return request<ApiResult<null>>(`/api/v1/roles/${id}/status${toQuery({ status })}`, {
    method: "PUT",
  });
}

export async function getRoleMenuIds(id: number) {
  const res = await request<ApiResult<number[]>>(`/api/v1/roles/${id}/menuIds`);
  return res.data || [];
}

export function assignRoleMenus(id: number, menuIds: number[]) {
  return request<ApiResult<null>>(`/api/v1/roles/${id}/menus`, {
    method: "PUT",
    body: JSON.stringify(menuIds),
  });
}

export async function pageLogs(params?: {
  pageNum?: number;
  pageSize?: number;
  keywords?: string;
  createTime?: string[];
}) {
  const res = await request<PageResult<LogRecord>>(`/api/v1/logs/page${toQuery(params)}`);
  return res.data || { list: [], total: 0 };
}

export async function listMenuOptions(onlyParent = false) {
  const res = await request<ApiResult<Option<number>[]>>(
    `/api/v1/menus/options${toQuery({ onlyParent })}`
  );
  return res.data || [];
}

export async function getMenuForm(id: number) {
  const res = await request<ApiResult<MenuFormData>>(`/api/v1/menus/${id}/form`);
  return res.data;
}

export async function createMenu(data: MenuFormData) {
  const result = await request<ApiResult<null>>("/api/v1/menus", {
    method: "POST",
    body: JSON.stringify(data),
  });
  invalidateCurrentUserMenuRoutes();
  return result;
}

export async function updateMenu(id: number, data: MenuFormData) {
  const result = await request<ApiResult<null>>(`/api/v1/menus/${id}`, {
    method: "PUT",
    body: JSON.stringify({ ...data, id }),
  });
  invalidateCurrentUserMenuRoutes();
  return result;
}

export async function deleteMenu(id: number) {
  const result = await request<ApiResult<null>>(`/api/v1/menus/${id}`, {
    method: "DELETE",
  });
  invalidateCurrentUserMenuRoutes();
  return result;
}

export async function pageDicts(params?: { pageNum?: number; pageSize?: number; keywords?: string }) {
  const res = await request<PageResult<DictRecord>>(`/api/v1/dicts/page${toQuery(params)}`);
  return res.data || { list: [], total: 0 };
}

export async function getDictForm(id: number) {
  const res = await request<ApiResult<DictFormData>>(`/api/v1/dicts/${id}/form`);
  return res.data;
}

export function createDict(data: DictFormData) {
  return request<ApiResult<null>>("/api/v1/dicts", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export function updateDict(id: number, data: DictFormData) {
  return request<ApiResult<null>>(`/api/v1/dicts/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export function deleteDicts(ids: number[]) {
  return request<ApiResult<null>>(`/api/v1/dicts/${ids.join(",")}`, {
    method: "DELETE",
  });
}

export async function pageDictItems(
  dictCode: string,
  params?: { pageNum?: number; pageSize?: number; keywords?: string }
) {
  const res = await request<PageResult<DictItemRecord>>(
    `/api/v1/dicts/${dictCode}/items/page${toQuery(params)}`
  );
  return res.data || { list: [], total: 0 };
}

export async function getDictItemForm(dictCode: string, itemId: number) {
  const res = await request<ApiResult<DictItemFormData>>(
    `/api/v1/dicts/${dictCode}/items/${itemId}/form`
  );
  return res.data;
}

export function createDictItem(dictCode: string, data: DictItemFormData) {
  return request<ApiResult<null>>(`/api/v1/dicts/${dictCode}/items`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export function updateDictItem(dictCode: string, itemId: number, data: DictItemFormData) {
  return request<ApiResult<null>>(`/api/v1/dicts/${dictCode}/items/${itemId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export function deleteDictItems(dictCode: string, itemIds: number[]) {
  return request<ApiResult<null>>(`/api/v1/dicts/${dictCode}/items/${itemIds.join(",")}`, {
    method: "DELETE",
  });
}

export async function login(username: string, password: string, captchaKey?: string, captchaCode?: string) {
  const res = await request<ApiResult<AuthenticationToken>>(
    `/api/v1/auth/login${toQuery({ username, password, captchaKey, captchaCode })}`,
    { method: "POST" }
  );
  return res.data;
}

export async function getCaptcha() {
  const res = await request<ApiResult<CaptchaInfo>>("/api/v1/auth/captcha");
  return res.data;
}

export function logout() {
  return request<ApiResult<null>>("/api/v1/auth/logout", {
    method: "DELETE",
  });
}
